<script setup>
import { cloneDeep, isEmpty, isEqual, isNull } from 'lodash'
import { computed, handleError, nextTick, ref, toValue, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import OkrFiltersApiHandler from '@/api/okr-filters'
import { EVENT_NAMES } from '@/tracking/gtm-helpers'
import { gtmTracker } from '@/tracking/gtm-tracking'
import {
  createPayloadForFeedCustomFilter,
  FEED_CUSTOM_FILTERS_KEYS,
  parseSavedFilterValues
} from '@/utils/check-ins/check-ins-feed'
import { useFetchGroups, useFetchUsers } from '@/utils/check-ins/schedule-composables'
import { DIALOG_TYPES } from '@/utils/components-configurations/app-dialog'
import { SAVING_INDICATOR_TYPES } from '@/utils/components-configurations/saving-indicator'
import { ASSIGNEE_ENTITY_KEYS, CHECKIN_ENTITY_KEYS } from '@/utils/entity-keys'
import { isStringEmpty } from '@/utils/general'
import { GLOBAL_GROUP_KEYS } from '@/utils/global-groups'
import { showNotify } from '@/utils/notify'
import { objectOrNullProp } from '@/utils/prop-validators'

import AppDialog from '@/components/AppDialog'
import FormCaption from '@/components/check-ins/FormCaption'
import ScheduleParticipantsSelect from '@/components/check-ins/ScheduleParticipantsSelect'
import AppFieldError from '@/components/form/AppFieldError'
import OwnerFieldOption from '@/components/form/OwnerFieldOption'
import GlobalGroupsSelectOption from '@/components/global-groups/GlobalGroupsSelectOption'
import ModalFooterActions from '@/components/objectives/forms/ModalFooterActions'
import SavingIndicator from '@/components/SavingIndicator'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppDivider from '@/components/ui/AppDivider'
import AppInput from '@/components/ui/AppInput/AppInput'
import Modal from '@/components/ui/Modal/Modal'
import {
  CHECKIN_FILTER_GROUPS_SELECT_TEST_ID,
  CHECKIN_FILTER_MODAL_DELETE_BUTTON_TEST_ID,
  CHECKIN_FILTER_MODAL_NAME_ERROR_TEST_ID,
  CHECKIN_FILTER_MODAL_NAME_INPUT_TEST_ID,
  CHECKIN_FILTER_OWNERS_SELECT_TEST_ID
} from '@/views/check-ins/jest-helpers'

defineOptions({
  name: 'CheckinFeedCustomFilterModal'
})

const props = defineProps({
  opened: {
    type: Boolean
  },

  filterData: {
    validator: v => objectOrNullProp(v),
    default: null
  }
})

const FILTER_NAME_MAX_LENGTH = 50

const { NAME } = CHECKIN_ENTITY_KEYS

const { USERS, GROUPS } = FEED_CUSTOM_FILTERS_KEYS

const openedDropdowns = ref({
  [USERS]: false,
  [GROUPS]: false
})

const isSomeFilterDropdownOpen = computed(() => {
  return Object.values(toValue(openedDropdowns)).some(isOpened => isOpened)
})

const DEFAULT_FORM_MODEL = {
  [CHECKIN_ENTITY_KEYS.NAME]: '',
  [USERS]: [],
  [GROUPS]: []
}

const formModel = ref(cloneDeep(DEFAULT_FORM_MODEL))

const modalContentBottomPadding = computed(() => {
  const DEFAULT_PADDING = '0px'

  if (!props.opened) return DEFAULT_PADDING

  return isSomeFilterDropdownOpen.value ? '200px' : DEFAULT_PADDING
})

const isSaving = ref(false)
const closeModalOnSavingFinished = ref(false)
const isConfirmCloseShow = ref(false)

const emit = defineEmits({
  'on-close': null,
  'on-filter-created': null,
  'on-filter-updated': null,
  'remove-filter': null
})

const areDataChanged = computed(() => {
  const source = isEdit.value ? filterSnapshot.value : DEFAULT_FORM_MODEL

  return !isEqual(formModel.value, source)
})

const checkConfirmationAndCloseModal = ({ checkDataChange = true } = {}) => {
  if (checkDataChange !== false && areDataChanged.value) {
    isConfirmCloseShow.value = true
  } else {
    emit('on-close')
  }
}

const hideConfirmCloseModal = () => {
  isConfirmCloseShow.value = false
}

const onConfirmClose = () => {
  hideConfirmCloseModal()
  checkConfirmationAndCloseModal({ checkDataChange: false })
}

const onClose = () => {
  if (isSaving.value) {
    closeModalOnSavingFinished.value = true
  } else {
    checkConfirmationAndCloseModal()
  }
}

const isEdit = computed(() => !isNull(props.filterData))

const nameInputReference = useTemplateRef('nameInput')

const onAfterEnter = async () => {
  if (!isEdit.value) {
    await nextTick()
    nameInputReference.value?.focus()
  }
}

const applyCreateState = () => {
  formModel.value = cloneDeep(DEFAULT_FORM_MODEL)
}

const filterSnapshot = ref(null)

const saveSnapshot = () => {
  filterSnapshot.value = cloneDeep(formModel.value)
}

const applyEditState = () => {
  const filterValues = parseSavedFilterValues({
    filterData: props.filterData,
    replaceFilterIdWithFilterKey: false
  })

  formModel.value[NAME] = props.filterData[NAME] || DEFAULT_FORM_MODEL[NAME]
  ;[USERS, GROUPS].forEach(key => {
    formModel.value[key] = filterValues[key] || DEFAULT_FORM_MODEL[key]
  })

  saveSnapshot()
}

const normalizeEntity = ({ selectedItems = [], list = [], itemValue = '' } = {}) => {
  if (isStringEmpty(itemValue) || !itemValue) {
    throw new Error('Item value is required for normalizeEntity function')
  }

  if (isEmpty(list)) return []

  if (isEmpty(selectedItems)) return selectedItems

  const availableItems = list.map(item => item[itemValue])

  return selectedItems.filter(item => availableItems.includes(item))
}

const normalizeEditState = () => {
  if (!isEdit.value) return

  formModel.value[USERS] = normalizeEntity({
    selectedItems: formModel.value[USERS],
    list: usersList.value,
    itemValue: ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID
  })

  formModel.value[GROUPS] = normalizeEntity({
    selectedItems: formModel.value[GROUPS],
    list: groupsList.value,
    itemValue: GLOBAL_GROUP_KEYS.ID
  })

  saveSnapshot()
}

const getGroupsAndUsers = async () => {
  await Promise.all([setGroupsList(), setUsersList()])

  normalizeEditState()
}

const onBeforeEnter = () => {
  if (isEdit.value) {
    applyEditState()
  } else {
    applyCreateState()
  }

  getGroupsAndUsers()
}

const isNameValid = ref(true)

const isCreateButtonDisabled = computed(() => {
  return isEdit.value && !areDataChanged.value
})

const isDataLoading = ref(false)

const { t } = useI18n()

const confirmButtonText = computed(() => {
  if (isEdit.value) {
    return t('action.save')
  }

  return t('action.create')
})

const savingIndicator = ref(null)
const savingIndicatorTop = ref(null)

const onSavingStarted = () => {
  isSaving.value = true
  savingIndicator.value.startSaving()
  savingIndicatorTop.value.startSaving()
}

const onSavingFinished = () => {
  isSaving.value = false
  savingIndicator.value.endSaving()
  savingIndicatorTop.value.endSaving()
  if (closeModalOnSavingFinished.value) {
    checkConfirmationAndCloseModal({ checkDataChange: false })
  }
}

const onConfirm = async () => {
  if (isCreateButtonDisabled.value) return

  isNameValid.value = !isStringEmpty(formModel.value[NAME])

  if (!isNameValid.value) return

  const api = new OkrFiltersApiHandler()

  try {
    closeModalOnSavingFinished.value = true

    onSavingStarted()

    const requestMethod = isEdit.value
      ? payload =>
          api.updateFilterViewForUser({
            filterViewId: props.filterData.id,
            ...props.filterData,
            ...payload
          })
      : payload => api.createCustomFilterForFeed(payload)

    const filters = createPayloadForFeedCustomFilter(formModel.value)

    const filter = await requestMethod({
      name: formModel.value[NAME].trim().slice(0, FILTER_NAME_MAX_LENGTH),
      filters
    })

    const notification = isEdit.value
      ? 'filters_saver.filter_updated'
      : 'filters_saver.filter_created'

    showNotify({ title: t(notification, { filterName: filter[NAME] }) })

    const eventName = isEdit.value
      ? EVENT_NAMES.CHECKIN_FILTER_UPDATED
      : EVENT_NAMES.CHECKIN_FILTER_CREATED

    gtmTracker.logEvent(eventName)

    const emitEvent = isEdit.value ? 'on-filter-updated' : 'on-filter-created'

    emit(emitEvent, filter)
  } catch (error) {
    closeModalOnSavingFinished.value = false
    handleError({ error })
  } finally {
    onSavingFinished()
  }
}

const { doFetch: getGroups, loading: isGroupsLoading } = useFetchGroups({
  selectedGroups: () => formModel.value[GROUPS]
})

const groupsList = ref([])

const setGroupsList = async () => {
  groupsList.value = await getGroups()
}

const { doFetch: getUsers, loading: isUsersLoading } = useFetchUsers({
  selectedUsers: () => formModel.value[USERS],
  isForAdmin: false
})

const usersList = ref([])

const setUsersList = async () => {
  usersList.value = await getUsers()
}

watch(
  () => props.opened,
  newValue => {
    if (!newValue) {
      formModel.value = cloneDeep(DEFAULT_FORM_MODEL)
      isNameValid.value = true
      filterSnapshot.value = null
    }
  }
)
</script>

<template>
  <Modal
    :data-testid="$attrs['data-testid'] || null"
    :scrollable-content="false"
    :show="opened"
    :style="{ '--modal-content-bottom-padding': modalContentBottomPadding }"
    class="fcf-Modal"
    manual-close
    size="lg-next"
    @close="onClose"
    @after-enter="onAfterEnter"
    @before-enter="onBeforeEnter"
  >
    <template #header>
      <div class="fcf-TitleWrapper">
        <AppInput
          ref="nameInput"
          v-model="formModel[NAME]"
          :data-auto-testid="CHECKIN_FILTER_MODAL_NAME_INPUT_TEST_ID"
          :data-testid="CHECKIN_FILTER_MODAL_NAME_INPUT_TEST_ID"
          :is-error="!isNameValid"
          :max-length="FILTER_NAME_MAX_LENGTH"
          :placeholder="t('checkins.feed.untitled_filter')"
          class="fcf-Title"
          show-error-for-placeholder
          style-type="title"
          @focus="isNameValid = true"
        />
        <AppFieldError
          v-if="!isNameValid"
          :data-auto-testid="CHECKIN_FILTER_MODAL_NAME_ERROR_TEST_ID"
          :data-testid="CHECKIN_FILTER_MODAL_NAME_ERROR_TEST_ID"
          :show="!isNameValid"
          class="fcf-TitleError"
        >
          {{ t('field.required') }}
        </AppFieldError>
      </div>
    </template>

    <template #before-close>
      <SavingIndicator ref="savingIndicator" />
    </template>

    <template #control>
      <AppButton
        v-if="isEdit"
        :data-auto-testid="CHECKIN_FILTER_MODAL_DELETE_BUTTON_TEST_ID"
        :data-testid="CHECKIN_FILTER_MODAL_DELETE_BUTTON_TEST_ID"
        icon="delete-next"
        size="sm"
        type="subtle"
        @click="emit('remove-filter', filterData)"
      />
    </template>

    <template #loader>
      <SavingIndicator ref="savingIndicatorTop" :type="SAVING_INDICATOR_TYPES.LINE" />
    </template>

    <div class="fcf-Content">
      <FormCaption
        :subtitle="t('checkins.feed.filter_modal.subtitle')"
        :title="t('checkins.feed.filter_modal.title')"
      >
        <ScheduleParticipantsSelect
          v-model="formModel[USERS]"
          :data-auto-testid="CHECKIN_FILTER_OWNERS_SELECT_TEST_ID"
          :data-testid="CHECKIN_FILTER_OWNERS_SELECT_TEST_ID"
          :item-label="ASSIGNEE_ENTITY_KEYS.NAME"
          :item-value="ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID"
          :label="t('common.choose', { entity: t('common.users').toLowerCase() })"
          :loading="isUsersLoading"
          :options="usersList"
          :search-function="getUsers"
          class="fcf-UsersSelect"
          empty-state-icon="user-next-small"
          @update:options="usersList = $event"
          @toggle-dropdown="openedDropdowns[USERS] = $event"
        >
          <template #option-label="{ option }">
            <OwnerFieldOption :label-key="ASSIGNEE_ENTITY_KEYS.NAME" :option="option" />
          </template>

          <template #empty-state-label>
            {{ t('filter.all_users') }}
          </template>
        </ScheduleParticipantsSelect>

        <ScheduleParticipantsSelect
          v-model="formModel[GROUPS]"
          :data-auto-testid="CHECKIN_FILTER_GROUPS_SELECT_TEST_ID"
          :data-testid="CHECKIN_FILTER_GROUPS_SELECT_TEST_ID"
          :item-label="GLOBAL_GROUP_KEYS.NAME"
          :item-value="GLOBAL_GROUP_KEYS.ID"
          :label="t('common.and_entity', { entity: t('filter.groups').toLowerCase() })"
          :loading="isGroupsLoading"
          :options="groupsList"
          :search-function="getGroups"
          empty-state-icon="team-next-small"
          @update:options="groupsList = $event"
          @toggle-dropdown="openedDropdowns[GROUPS] = $event"
        >
          <template #option-label="{ option }">
            <GlobalGroupsSelectOption v-if="option" :group="option" />
          </template>

          <template #empty-state-label>
            {{ t('filter.all_groups') }}
          </template>

          <template #error>
            <AppFieldError show>
              {{ t('schedule.participants.empty_segments_error') }}
            </AppFieldError>
          </template>
        </ScheduleParticipantsSelect>
      </FormCaption>
    </div>

    <template #footer>
      <div class="fcf-Footer">
        <AppDivider class="fcf-Footer_Divider" no-margin />
        <ModalFooterActions
          :disabled="isCreateButtonDisabled"
          :loading="isSaving || isDataLoading"
          hide-checkbox
          @close="onClose"
          @create="onConfirm"
        >
          <template #confirm-button-text> {{ confirmButtonText }}</template>
        </ModalFooterActions>
      </div>
    </template>
  </Modal>

  <portal to="modal-windows">
    <AppDialog
      :show="isConfirmCloseShow"
      :title="$t('confirm_modal.title')"
      :type="DIALOG_TYPES.WARNING"
      @on-close="hideConfirmCloseModal"
      @on-confirm="onConfirmClose"
    >
      {{ $t('confirm_modal.description') }}

      <template #confirm-btn-text>
        {{ $t('confirm.discard_btn') }}
      </template>
    </AppDialog>
  </portal>
</template>

<style lang="scss" scoped>
@import '~@/assets/styles/schedule-checkin-modal';

.fcf-Modal {
  @extend %modal-styles;

  &:deep(.uim-Actions) {
    margin: 0;
  }
}

.fcf-Title {
  @extend %modal-input-title-styles;
}

.fcf-Footer {
  @extend %modal-footer-styles;
}

.fcf-TitleWrapper {
  @extend %modal-title-wrapper-styles;
}

.fcf-Footer_Divider {
  @extend %modal-footer-divider-styles;
}

.fcf-Content {
  padding-top: 25px;
  --fc-caption-content-mt: 16px;
}

.fcf-TitleError {
  @extend %modal-input-title-error-styles;
}

.fcf-UsersSelect {
  margin-bottom: 16px;
}
</style>
